import { z } from 'zod';

/**
 * Catalogue d'avatars intégrés (spec 07 §2). Source unique de vérité :
 * les fichiers vivent dans `apps/web/public/avatars/<id>.webp|png|mp4` (256×256),
 * servis statiquement par le front. L'API valide les ids via `avatarIdField`,
 * jamais d'URL libre.
 *
 * Chaque avatar existe en trois déclinaisons pour le MÊME id : `webp`/`png`
 * (image, vignette + fallback) et `mp4` (animation). L'affichage lance la vidéo
 * (image en vignette le temps du chargement) — voir composant `Avatar` (spec 07 §2.3).
 */

export const AVATAR_COUNT = 36;

/** Ids du catalogue : "1" … "36". */
export const AVATAR_IDS: readonly string[] = Object.freeze(
  Array.from({ length: AVATAR_COUNT }, (_, i) => String(i + 1)),
);

export function isAvatarId(value: unknown): value is string {
  return typeof value === 'string' && AVATAR_IDS.includes(value);
}

/** Champ zod : un id du catalogue, rien d'autre. */
export const avatarIdField = z
  .string()
  .refine(isAvatarId, { message: 'Avatar inconnu au catalogue' });

/** Base des assets (relative à l'origine du front). */
export const AVATAR_BASE_PATH = '/avatars';

/** Format préférentiel (plus léger) — à proposer via `<source type="image/webp">`. */
export function avatarWebpUrl(id: string): string {
  return `${AVATAR_BASE_PATH}/${id}.webp`;
}

/** Fallback universel — `<img src>` ; le navigateur choisit (spec 07 §2.3). */
export function avatarPngUrl(id: string): string {
  return `${AVATAR_BASE_PATH}/${id}.png`;
}

/** Version animée — `<video>` muet/bouclé, l'image servant de vignette (spec 07 §2.3). */
export function avatarVideoUrl(id: string): string {
  return `${AVATAR_BASE_PATH}/${id}.mp4`;
}
